import React from 'react';
import PropTypes from 'prop-types';
import { formatNumericDate, getTimeInHoursAndMins } from '../../utils/dates';
import Search from '../../componentsV2/SearchBar/Search';
import Table from '../../componentsV2/table/Table';
import Button from '../../componentsV2/Button/Button';
import { Plus } from '../../componentsV2/Icons/Icons';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import Pagination from '../../componentsV2/pagination/Pagination';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';

const DEFAULT_ROUTES = [];

const RoutesPage = ({
  routes = DEFAULT_ROUTES,
  totalRoutes = 0,
  totalUnfilteredRoutes = 0,
  onSearch = () => { },
  onSort = () => { },
  direction = 'asc',
  handleOpenAddRouteModal,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  activeSearchParams = {},
}) => {
  // we could use this function to get query params from the url during later stages of development
  // const getQueryParams = (param) => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   return urlParams.get(param);
  // };

  const mappedActiveSearchParams = {
    keyword: activeSearchParams.routeKeyword || '',
    name: activeSearchParams.routeName || '',
    dateFrom: activeSearchParams.routesStartDate || '',
    dateTo: activeSearchParams.routesEndDate || '',
  };

  const currentOffset = (currentPage - 1) * itemsPerPage;

  const columns = [
    {
      title: 'Name', dataIndex: 'name', className: 'w-1/3', isSortable: true, onSort: () => onSort({ field: 'name', order: direction === 'asc' ? 'desc' : 'asc' }),
    },
    {
      title: 'Date', dataIndex: 'date', render: (text) => formatNumericDate(text), className: 'w-1/6', isSortable: true, onSort: () => onSort({ field: 'date', order: direction === 'asc' ? 'desc' : 'asc' }),
    },
    {
      title: 'Sites', dataIndex: 'number_of_stops', className: 'w-1/6', isSortable: true, onSort: () => onSort({ field: 'number_of_stops', order: direction === 'asc' ? 'desc' : 'asc' }),
    },
    {
      title: 'Distance', dataIndex: 'total_distance', render: (text) => (Number.isNaN(text) || text === 'NaN' ? 'N/A' : `${Number(text).toFixed(2)} mi`), className: 'w-1/6', isSortable: true, onSort: () => onSort({ field: 'total_distance', order: direction === 'asc' ? 'desc' : 'asc' }),
    },
    {
      title: 'Time', dataIndex: 'total_time', render: (text) => getTimeInHoursAndMins(text), className: 'w-1/6', isSortable: true, onSort: () => onSort({ field: 'total_time', order: direction === 'asc' ? 'desc' : 'asc' }),
    },
  ];

  const searchFields = [
    { label: 'Keyword', name: 'keyword', type: 'text' },
    { label: 'Name', name: 'name', type: 'text' },
    { label: 'Date From', name: 'dateFrom', type: 'date' },
    { label: 'Date To', name: 'dateTo', type: 'date' },
  ];

  const chipsSettings = {
    keyword: {
      prefix: 'keyword',
      type: 'text',
    },
    name: {
      prefix: 'route name',
      type: 'text',
    },
    dateFrom: {
      prefix: 'from',
      type: 'date',
    },
    dateTo: {
      prefix: 'to',
      type: 'date',
    },
  };

  const breadcrumbLinks = [
    { href: '/routes', name: 'Routes' },
  ];

  let contentToRender;

  if (routes.length > 0) {
    contentToRender = (
      <>
        <div className="flex w-full gap-4 justify-between items-center pb-4">
          <PagesHeader name="route" label="Routes" size="h-9" />
          {totalRoutes > 0 && (
            <div className="flex-1 flex justify-center">
              <ResultCount
                offset={currentOffset}
                pageSize={routes.length}
                total={totalRoutes}
                unfilteredTotal={totalUnfilteredRoutes}
                viewType="table"
                pageType="routes"
              />
            </div>
          )}
          <Button label="New Route" Icon={Plus} onClick={handleOpenAddRouteModal} />
        </div>
        <Table
          dataSource={routes}
          columns={columns}
          onSort={onSort}
          typeOfData="routes"
          className="w-full table-fixed"
        />
        <footer>
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={totalRoutes}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            marginTop="my-9"
          />
        </footer>
      </>
    );
  } else if (totalUnfilteredRoutes === 0) {
    contentToRender = (
      <EmptyState pageType="routes" onAdd={handleOpenAddRouteModal} />
    );
  } else {
    contentToRender = <NoSearchResults pageType="routes" />;
  }

  return (
    <div>
      <div className="ml-2.75">
        <header>
          <div className="flex justify-between items-start w-full">
            {/* Breadcrumbs */}
            <Breadcrumbs links={breadcrumbLinks} />
            {/* Search Section */}
            <div className="self-start">
              <Search onSearch={onSearch} routes={routes} fields={searchFields} chipsSettings={chipsSettings} title="search routes" activeSearchParams={mappedActiveSearchParams} />
            </div>
          </div>
        </header>
        {contentToRender}
      </div>
    </div>

  );
};

RoutesPage.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.string,
    number_of_stops: PropTypes.number,
    total_distance: PropTypes.number,
    total_time: PropTypes.number,
  })),
  totalRoutes: PropTypes.number,
  totalUnfilteredRoutes: PropTypes.number,
  onSearch: PropTypes.func,
  onSort: PropTypes.func,
  handleOpenAddRouteModal: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  direction: PropTypes.string,
  activeSearchParams: PropTypes.object,
};

export default RoutesPage;
